import { Loader2, X } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent } from 'react';

import {
  MAX_PRESCRIPTION_IMAGE_BYTES,
  prescriptionApi,
  UPLOAD_ERROR,
  type UploadedImageKey,
} from '@/entities/prescription/api/prescription.api';
import {
  useInvalidatePrescriptionQueries,
  usePresignedUrls,
  useUploadComplete,
} from '@/entities/prescription/hooks/usePrescriptionImages';
import {
  convertPdfToJpegPages,
  isPdfFile,
} from '@/entities/prescription/lib/pdfToJpegPages';
import { getUploadSettlementYearMonth } from '@/entities/prescription/lib/uploadSettlementMonth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { getApiErrorCode } from '@/shared/api/http-client';
import { cn } from '@/shared/lib/utils';
import { formatFileSize, formatYm } from '@/shared/utils/format';
import { toastApi } from '@/shared/utils/toast';
import type { PrescriptionUploadTarget } from '@/stores/uiStore';

/**
 * 업로드 조건 (M-06): JPG · PNG · PDF, 건당 10MB, 1회 최대 20장.
 * 10MB는 서버도 완료 때 막는다(PRESCRIPTION_IMAGE_TOO_LARGE). 형식 · 개수는 웹만 막는다 (C1)
 */
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];
const MAX_FILE_SIZE = MAX_PRESCRIPTION_IMAGE_BYTES;
const MAX_FILES = 20;
/** S3 PUT 동시 업로드 수 */
const UPLOAD_CONCURRENCY = 3;

type QueueStatus =
  'converting' | 'ready' | 'invalid' | 'uploading' | 'done' | 'failed';

interface QueueItem {
  id: number;
  file: File;
  status: QueueStatus;
  /** invalid일 때 사유 */
  error?: string;
  /** uploading일 때 0~100 */
  progress: number;
  /** 이미지 미리보기(object URL). PDF 변환 중 · 이미지가 아닌 오류 파일은 없다 */
  previewUrl?: string;
  /** converting일 때 PDF 페이지 변환 진행 */
  convert?: { done: number; total: number };
}

const getValidationError = (file: File, acceptedCount: number) => {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!ALLOWED_EXTENSIONS.includes(ext))
    return 'JPG · PNG · PDF 파일만 등록할 수 있어요';
  if (file.size > MAX_FILE_SIZE) return '건당 10MB를 넘어 등록할 수 없어요';
  if (acceptedCount >= MAX_FILES)
    return `한 번에 최대 ${MAX_FILES}장까지 등록할 수 있어요`;
  return undefined;
};

/** 업로드 대기 목록의 한 행 (M-06 · M-07 S1/S2) */
const QueueRow = ({
  item,
  disabled,
  onRemove,
  onRetry,
  onPreview,
}: {
  item: QueueItem;
  disabled: boolean;
  onRemove: () => void;
  onRetry: () => void;
  onPreview: () => void;
}) => {
  const isError = item.status === 'invalid' || item.status === 'failed';
  return (
    <li
      className={cn(
        'flex flex-col rounded-md border px-3.5 py-[11px]',
        isError
          ? 'border-[#f7c9c9] bg-[#fff7f7]'
          : 'border-line-soft bg-subtle',
      )}
    >
      <div className="flex items-center gap-3">
        {item.previewUrl ? (
          // 썸네일 — 누르면 크게 본다
          <button
            type="button"
            onClick={onPreview}
            aria-label={`${item.file.name} 미리보기`}
            className="size-11 shrink-0 overflow-hidden rounded-[6px] border border-line bg-surface outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <img
              src={item.previewUrl}
              alt=""
              className="size-full object-contain"
              draggable={false}
            />
          </button>
        ) : (
          <div
            className={cn(
              'flex size-11 shrink-0 items-center justify-center rounded-[6px]',
              isError ? 'bg-[#f7dcdc]' : 'bg-[#e6eaf0]',
            )}
          >
            {item.status === 'converting' && (
              <Loader2 className="size-4 animate-spin text-ink-6" aria-hidden />
            )}
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <p
            className={cn(
              'truncate text-[13px] leading-5',
              isError ? 'text-[#8a5a5a]' : 'text-ink',
            )}
          >
            {item.file.name}
          </p>
          {item.status === 'invalid' && (
            <p className="text-[11px] leading-[17px] text-danger">
              {item.error}
            </p>
          )}
          {item.status === 'converting' && (
            <p className="text-[11px] leading-[17px] text-ink-6">
              PDF를 페이지별 이미지로 바꾸는 중
              {item.convert && item.convert.total > 0
                ? ` ${item.convert.done}/${item.convert.total}`
                : ''}
            </p>
          )}
        </div>
        <span
          className={cn(
            'shrink-0 text-xs leading-[18px]',
            item.status === 'invalid' ? 'text-[#c98a8a]' : 'text-ink-7',
          )}
        >
          {item.status === 'converting' ? '' : formatFileSize(item.file.size)}
        </span>
        {item.status === 'ready' && <Badge tone="success">준비됨</Badge>}
        {item.status === 'done' && <Badge tone="success">완료</Badge>}
        {item.status === 'uploading' && (
          <span className="w-9 shrink-0 text-right text-[11px] leading-[17px] font-medium text-primary">
            {item.progress}%
          </span>
        )}
        {item.status === 'failed' && (
          <>
            <Badge tone="danger">실패</Badge>
            <button
              type="button"
              disabled={disabled}
              onClick={onRetry}
              className="shrink-0 text-xs leading-[18px] font-medium text-primary disabled:opacity-50"
            >
              재시도
            </button>
          </>
        )}
        {(item.status === 'ready' ||
          item.status === 'invalid' ||
          item.status === 'converting') && (
          <button
            type="button"
            disabled={disabled}
            onClick={onRemove}
            className="shrink-0 text-xs leading-[18px] font-medium text-danger disabled:opacity-50"
          >
            삭제
          </button>
        )}
      </div>
      {item.status === 'uploading' && (
        <div className="mt-[9px] h-1.5 w-full max-w-[400px] overflow-hidden rounded-[3px] bg-[#e6eaf0]">
          <div
            className="h-full rounded-[3px] bg-primary transition-[width]"
            style={{ width: `${item.progress}%` }}
          />
        </div>
      )}
    </li>
  );
};

/**
 * M-06 처방 내역 등록 모달 (+ M-07 S1 등록 중 · S2 일부 실패 · S3 완료, 중단 확인 팝업)
 * 업로드 순서: presigned-urls → 파일별 S3 PUT(동시 3개, 진행률) → 성공한 파일만 upload-complete
 */
export const PrescriptionUploadDialog = ({
  target,
  onClose,
}: {
  target: PrescriptionUploadTarget;
  onClose: () => void;
}) => {
  // 업로드 전 안내용 — 모달을 연 시점 기준으로 고정한다. 완료 뒤에는 서버 응답 값을 쓴다
  const [expectedSettlementYm] = useState(() => getUploadSettlementYearMonth());
  /** 마지막 완료 응답의 정산월 — 완료 스낵바에 쓴다 */
  const registeredSettlementYm = useRef<string | null>(null);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState({ done: 0, total: 0 });
  const [dragging, setDragging] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [preview, setPreview] = useState<QueueItem | null>(null);
  const nextId = useRef(1);
  /** 만든 미리보기 URL — 모달이 닫힐 때 한꺼번에 해제한다 */
  const previewUrls = useRef<string[]>([]);
  /** 이번 모달에서 등록 완료된 누적 장수 (재시도 포함) — 완료 스낵바에 쓴다 */
  const registeredCount = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const presignedUrls = usePresignedUrls();
  const uploadComplete = useUploadComplete();
  const invalidate = useInvalidatePrescriptionQueries();

  const readyCount = items.filter((i) => i.status === 'ready').length;
  const invalidCount = items.filter((i) => i.status === 'invalid').length;
  const failedCount = items.filter((i) => i.status === 'failed').length;
  const convertingCount = items.filter((i) => i.status === 'converting').length;
  /** 중단하면 사라지는 장수 — 오류 건은 어차피 등록되지 않으므로 세지 않는다 */
  const pendingCount = readyCount + failedCount;

  useEffect(
    () => () => {
      previewUrls.current.forEach((u) => URL.revokeObjectURL(u));
    },
    [],
  );

  // S1 등록 중에는 새로고침 · 탭 닫기를 브라우저 이탈 경고로 막는다
  useEffect(() => {
    if (!uploading) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploading]);

  const patchItems = (ids: number[], patch: Partial<QueueItem>) =>
    setItems((prev) =>
      prev.map((i) => (ids.includes(i.id) ? { ...i, ...patch } : i)),
    );

  const makePreview = (file: File) => {
    const url = URL.createObjectURL(file);
    previewUrls.current.push(url);
    return url;
  };

  /** 검증해서 대기 목록 항목으로 만든다. 통과한 이미지는 미리보기 URL을 붙인다 */
  const toQueueItems = (files: File[], acceptedSoFar: number) => {
    let accepted = acceptedSoFar;
    return files.map((file): QueueItem => {
      const error = getValidationError(file, accepted);
      if (!error) accepted += 1;
      return {
        id: nextId.current++,
        file,
        status: error ? 'invalid' : 'ready',
        error,
        progress: 0,
        previewUrl: error ? undefined : makePreview(file),
      };
    });
  };

  const countAccepted = (list: QueueItem[]) =>
    list.filter((i) => i.status === 'ready' || i.status === 'failed').length;

  /**
   * 파일 추가 — PDF는 앱과 같이 페이지별 JPEG로 바꿔 넣는다 (원본 PDF는 올리지 않음).
   * 변환하는 동안은 PDF 자리에 진행 행을 두고, 끝나면 페이지 이미지 행들로 바꾼다.
   */
  const addFiles = (files: FileList | File[] | null) => {
    if (!files || uploading) return;
    const list = Array.from(files);
    if (list.length === 0) return;

    const pdfs = list.filter(isPdfFile);
    const images = list.filter((f) => !isPdfFile(f));
    const placeholders = pdfs.map((file): QueueItem => ({
      id: nextId.current++,
      file,
      status: 'converting',
      progress: 0,
      convert: { done: 0, total: 0 },
    }));
    setItems((prev) => [
      ...prev,
      ...toQueueItems(images, countAccepted(prev)),
      ...placeholders,
    ]);

    placeholders.forEach((ph) => {
      convertPdfToJpegPages(ph.file, (done, total) =>
        patchItems([ph.id], { convert: { done, total } }),
      )
        .then((pages) =>
          setItems((prev) => {
            const at = prev.findIndex((i) => i.id === ph.id);
            if (at < 0) return prev; // 변환 중에 지운 경우
            const rest = prev.filter((i) => i.id !== ph.id);
            const pageItems = toQueueItems(pages, countAccepted(rest));
            return [...prev.slice(0, at), ...pageItems, ...prev.slice(at + 1)];
          }),
        )
        .catch(() =>
          patchItems([ph.id], {
            status: 'invalid',
            error: 'PDF를 이미지로 바꾸지 못했어요. 파일을 확인해 주세요',
            convert: undefined,
          }),
        );
    });
  };

  const finishWithToast = () => {
    const ym = registeredSettlementYm.current ?? expectedSettlementYm;
    const viewing = target.viewingSettlementYearMonth;
    // 실적 화면에서 보던 달과 다른 정산월로 들어갔으면 어디로 갔는지 알려 준다
    const elsewhere =
      viewing && viewing !== ym
        ? ` 보고 있는 ${formatYm(viewing)}이 아닌 ${formatYm(ym)} 실적에서 확인할 수 있어요.`
        : '';
    toastApi.success(
      `${registeredCount.current}장이 등록되었습니다. ${formatYm(ym)} 정산분으로 자동 분류됐어요.${elsewhere}`,
      elsewhere ? 5 : 3,
    );
    onClose();
  };

  const runUpload = async (targets: QueueItem[]) => {
    if (targets.length === 0 || uploading) return;
    const ids = targets.map((t) => t.id);
    setUploading(true);
    setUploadStep({ done: 0, total: targets.length });
    patchItems(ids, { status: 'uploading', progress: 0 });

    const succeeded: Array<{ id: number } & UploadedImageKey> = [];
    let hasFailure = false;

    try {
      const urls = await presignedUrls.mutateAsync({
        relationshipId: target.relationshipId,
        filenames: targets.map((t) => t.file.name),
      });
      // 응답은 요청한 filenames 순서와 같다. 같은 이름이 여러 개일 수 있어 순서로 맞춘다
      let cursor = 0;
      const worker = async () => {
        while (cursor < targets.length) {
          const index = cursor++;
          const item = targets[index];
          const url = urls[index];
          try {
            if (!url) throw new Error('presigned URL 없음');
            await prescriptionApi.uploadToS3(url.presignedUrl, item.file, (p) =>
              patchItems([item.id], { progress: Math.min(p, 99) }),
            );
            succeeded.push({
              id: item.id,
              shortKey: url.shortKey,
              uploadKey: url.uploadKey,
              uploadToken: url.uploadToken,
            });
          } catch {
            hasFailure = true;
            patchItems([item.id], { status: 'failed', progress: 0 });
          } finally {
            setUploadStep((s) => ({ ...s, done: s.done + 1 }));
          }
        }
      };
      await Promise.all(
        Array.from(
          { length: Math.min(UPLOAD_CONCURRENCY, targets.length) },
          worker,
        ),
      );

      if (succeeded.length > 0) {
        const succeededIds = succeeded.map((s) => s.id);
        try {
          // 발급 응답의 세 값을 그대로 보낸다 (하나라도 바뀌면 400)
          const result = await uploadComplete.mutateAsync({
            relationshipId: target.relationshipId,
            images: succeeded.map(({ shortKey, uploadKey, uploadToken }) => ({
              shortKey,
              uploadKey,
              uploadToken,
            })),
          });
          registeredCount.current += succeeded.length;
          registeredSettlementYm.current = result.settlementYearMonth;
          patchItems(succeededIds, { status: 'done', progress: 100 });
          void invalidate();
        } catch (error) {
          // 전부 저장되지 않는다 — 다시 올리면 새 키로 발급부터 한다
          hasFailure = true;
          if (getApiErrorCode(error) === UPLOAD_ERROR.TOO_LARGE) {
            // 서버가 올린 파일을 지웠다. 10MB 넘는 파일을 가려낼 수 없으므로 전부 다시 고르게 한다
            patchItems(succeededIds, {
              status: 'invalid',
              error: '건당 10MB를 넘는 이미지가 있어 등록하지 못했어요',
              progress: 0,
            });
          } else {
            patchItems(succeededIds, { status: 'failed', progress: 0 });
          }
        }
      }
    } catch {
      hasFailure = true;
      patchItems(ids, { status: 'failed', progress: 0 });
    } finally {
      setUploading(false);
    }

    // S3 전체 완료 — 실패분이 없으면 닫고 스낵바 (오류 건은 제외하고 등록한 것으로 본다)
    if (!hasFailure && registeredCount.current > 0) finishWithToast();
  };

  const startUpload = () =>
    runUpload(items.filter((i) => i.status === 'ready'));
  const retryAll = () => runUpload(items.filter((i) => i.status === 'failed'));

  /** ✕ · ESC · 배경 · 취소 — 등록 중에는 막고, 대기 목록이 있으면 중단 확인 */
  const requestClose = () => {
    if (uploading) return;
    if (pendingCount > 0) {
      setConfirmClose(true);
      return;
    }
    if (registeredCount.current > 0) finishWithToast();
    else onClose();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && requestClose()}>
        <DialogContent
          hideClose
          className="lg:max-w-[680px] lg:rounded-[20px]"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
        >
          {/* 헤더 — 제목 · 정산 월 고정 */}
          <div className="shrink-0 px-5 pt-6 lg:px-8 lg:pt-7">
            <div className="flex items-center">
              <DialogTitle className="text-xl leading-[30px]">
                처방 내역 등록
              </DialogTitle>
              <button
                type="button"
                onClick={requestClose}
                disabled={uploading}
                aria-label="닫기"
                className="ml-auto text-ink-6 hover:text-ink disabled:opacity-40"
              >
                <X className="size-5" />
              </button>
            </div>
            <DialogDescription className="mt-2 text-ink-4">
              {target.clinicName} · 올린 처방전 이미지는 아래 정산 월로 자동
              분류됩니다.
            </DialogDescription>
            <div className="mt-6 flex flex-col gap-3 rounded-lg border border-[#dce4f7] bg-primary-tint px-5 py-4 sm:flex-row sm:items-center sm:gap-5">
              <div className="flex w-[150px] shrink-0 flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs leading-[18px] text-[#5b6b8c]">
                    정산 월
                  </span>
                  <Badge className="bg-primary text-white">자동 지정</Badge>
                </div>
                <p className="text-[22px] leading-[33px] font-bold text-[#1f3461]">
                  {formatYm(expectedSettlementYm)}
                </p>
              </div>
              <div className="hidden h-11 w-px shrink-0 bg-[#dce4f7] sm:block" />
              <div className="text-xs leading-[19px] text-[#44557a]">
                <p>
                  등록한 날짜에 따라 정산 월이 자동으로 정해집니다. 매월
                  1일~10일 등록분과 11일~말일 등록분의 정산 월이 다르며,
                  사용자가 고를 수 없습니다.
                </p>
                <p>
                  정산 월을 바꾸려면 등록한 이미지를 삭제하고 해당 기간에 다시
                  등록해야 합니다.
                </p>
              </div>
            </div>
          </div>

          {/* 본문 — 드롭존 · 업로드 대기 목록만 스크롤 */}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-5 pb-2 lg:px-8 lg:pt-10">
            <div
              onDragEnter={(e) => {
                e.preventDefault();
                if (!uploading) setDragging(true);
              }}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node))
                  setDragging(false);
              }}
              onDrop={handleDrop}
              className={cn(
                'flex flex-col items-center justify-center rounded-xl border-[1.5px] border-dashed px-4 py-6 text-center transition-colors lg:py-[34px]',
                dragging
                  ? 'border-primary bg-primary-tint'
                  : 'border-[#c9d3de] bg-[#fafbfc]',
              )}
            >
              <p className="hidden text-[15px] leading-[23px] font-medium text-ink-2 lg:block">
                처방전 이미지를 여기로 끌어다 놓으세요
              </p>
              <p className="text-[15px] leading-[23px] font-medium text-ink-2 lg:hidden">
                처방전 이미지를 선택해 주세요
              </p>
              <p className="mt-2 text-xs leading-[18px] text-[#8a98a6]">
                JPG · PNG · PDF · 건당 10MB 이하 · 여러 장 동시 업로드 가능
                <br />
                PDF는 페이지마다 이미지 한 장으로 바뀌어 등록돼요
              </p>
              <Button
                variant="outline"
                className="mt-[18px] h-[42px] w-[120px] border-ink"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                파일 선택
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                className="hidden"
                data-testid="prescription-file-input"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </div>

            {items.length > 0 && (
              <section className="mt-[22px]">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-[13px] leading-5 font-medium text-ink-2">
                    업로드 대기
                  </h3>
                  {readyCount > 0 && (
                    <Badge tone="primary">{readyCount}장 준비</Badge>
                  )}
                  {invalidCount > 0 && (
                    <Badge tone="danger">{invalidCount}건 오류</Badge>
                  )}
                  <p className="ml-auto text-[11px] leading-[17px] text-ink-7">
                    최대 {MAX_FILES}장 ·{' '}
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => setItems([])}
                      className="hover:text-ink-3 hover:underline disabled:opacity-50"
                    >
                      모두 지우기
                    </button>
                  </p>
                </div>
                <ul className="mt-2.5 flex flex-col gap-2">
                  {items.map((item) => (
                    <QueueRow
                      key={item.id}
                      item={item}
                      disabled={uploading}
                      onRemove={() =>
                        setItems((prev) => prev.filter((i) => i.id !== item.id))
                      }
                      onRetry={() => runUpload([item])}
                      onPreview={() => setPreview(item)}
                    />
                  ))}
                </ul>
                {failedCount > 0 && !uploading && (
                  <div className="mt-2.5 flex items-center gap-2.5 rounded-[9px] bg-[#fff0f0] px-3.5 py-2.5">
                    <p className="text-[11px] leading-[17px] text-danger">
                      {failedCount}장이 등록되지 않았어요. 연결을 확인하고 다시
                      시도해주세요.
                    </p>
                    <button
                      type="button"
                      onClick={retryAll}
                      className="ml-auto shrink-0 text-[11px] leading-[17px] font-medium text-danger hover:underline"
                    >
                      전체 재시도
                    </button>
                  </div>
                )}
                <p className="mt-[18px] text-xs leading-[19px] text-ink-6">
                  {invalidCount > 0 &&
                    `오류 ${invalidCount}건은 제외하고 등록합니다. `}
                  등록 후에는 실적의 처방 이미지 탭에서 이미지를 추가하거나
                  삭제할 수 있습니다. 정산 월은 바뀌지 않습니다.
                </p>
              </section>
            )}
          </div>

          {/* 푸터 고정 */}
          <div className="flex shrink-0 gap-2.5 px-5 pt-4 pb-6 lg:px-8 lg:pb-7">
            <Button
              variant="secondary"
              size="xl"
              className="h-[50px] flex-1 text-sm lg:ml-auto lg:w-[110px] lg:flex-none"
              disabled={uploading}
              onClick={requestClose}
            >
              취소
            </Button>
            <Button
              size="xl"
              className="h-[50px] flex-[2] text-sm lg:w-[160px] lg:flex-none"
              disabled={!uploading && (readyCount === 0 || convertingCount > 0)}
              loading={uploading}
              onClick={startUpload}
            >
              {uploading
                ? `등록 중 ${Math.min(uploadStep.done + 1, uploadStep.total)}/${uploadStep.total}`
                : `${readyCount}장 등록하기`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 썸네일 크게 보기 */}
      <Dialog
        open={preview !== null}
        onOpenChange={(o) => !o && setPreview(null)}
      >
        <DialogContent className="lg:max-w-[720px]">
          <div className="flex shrink-0 items-center border-b border-line py-[18px] pr-14 pl-6">
            <DialogTitle className="truncate text-base leading-6 font-bold">
              {preview?.file.name}
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            업로드 대기 중인 처방전 이미지 미리보기
          </DialogDescription>
          <div className="min-h-0 flex-1 bg-field p-4 lg:h-[min(72vh,780px)] lg:flex-none">
            {preview?.previewUrl && (
              <img
                src={preview.previewUrl}
                alt={preview.file.name}
                className="size-full object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* M-07 중단 확인 팝업 */}
      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent className="max-w-[392px]">
          <AlertDialogTitle>등록을 중단할까요?</AlertDialogTitle>
          <AlertDialogDescription>
            업로드 대기 중인 {pendingCount}장이 사라집니다. 등록한 이미지는
            그대로 남습니다.
          </AlertDialogDescription>
          <AlertDialogFooter className="flex-col [&>*]:flex-none">
            <AlertDialogAction>계속 등록하기</AlertDialogAction>
            <AlertDialogCancel
              onClick={() => {
                if (registeredCount.current > 0) finishWithToast();
                else onClose();
              }}
            >
              중단하고 나가기
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
